import { EnabledProctoringOptions } from "@verity/api-client/types";
import ExtensionMode from "../images/browserMode.svg?react";
import LiveProctorMode from "../images/liveProctorMode.svg?react";
import LivePopInMode from "../images/livePopInMode.svg?react";
import LivePopInPostReviewMode from "../images/livePopInPostReviewMode.svg?react";
import { ProctoringAssessmentConfigurationV2 } from "@verity/proctoring-option/types";
import { CoreModal, CorePaper } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import { Dispatch, SetStateAction, useState } from "react";
import i18nPackage from "../../i18n/i18n";
import { VerityInstituteSettings } from "../../types/common";
import { Tooltip, Typography } from "@mui/material";
import { ConfigurationPageName, StandaloneConfigurationPageName } from "../proctoring-options/option-text";
import styles from "./method-step.module.css";
import { EditQuizButton } from "./platform-step";

function DeleteLiveSessionsModal({ onClose }: { onClose: () => void }) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18nPackage.t("editQuizPage.methodStep.deleteModalTitle")}
			showBottomCloseButton
			maxWidth="500px"
		>
			{i18nPackage.t("editQuizPage.methodStep.deleteUpcomingSessions")}
		</CoreModal>
	);
}

export default function MethodStep({
	verityInstituteSettings,
	enabledProctoringOptions,
	options,
	updateOptions,
	hasUpcomingSessions,
	updateCompletedSteps,
}: {
	verityInstituteSettings: VerityInstituteSettings;
	enabledProctoringOptions: EnabledProctoringOptions;
	options: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	updateOptions: (newOptions: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">) => void;
	hasUpcomingSessions: () => boolean;
	updateCompletedSteps: Dispatch<SetStateAction<Set<ConfigurationPageName | StandaloneConfigurationPageName>>>;
}) {
	const [deleteSessionsModalShowing, setDeleteSessionsModalShowing] = useState(false);
	const isMobileLayout = useMobileLayout();

	const setLiveMode = (mode: "live" | "pop-in" | "pop-in-post-review" | "automated") => {
		if (mode !== "live" && options.isLiveProctored && hasUpcomingSessions()) {
			setDeleteSessionsModalShowing(true);
		} else {
			updateOptions({
				...options,
				isLiveProctored: mode === "live",
				isLivePopIn: mode === "pop-in" || mode === "pop-in-post-review",
				enablePostReview: mode === "pop-in-post-review",
			});
		}
	};

	const disableAutomated = !enabledProctoringOptions.proctoringMethod.includes("automated");
	const automatedButton = (
		<EditQuizButton
			onClick={() => {
				if (options.isLiveProctored || options.isLivePopIn) {
					updateCompletedSteps((prev) => {
						const next = new Set(prev);
						// Remove live sessions step from completed steps if it is there
						// This happens if user created a live session, did all the other steps, but then decided to change to some other method
						next.delete("Live Sessions");
						return next;
					});
					setLiveMode("automated");
				}
			}}
			checked={!options.isLiveProctored && !options.isLivePopIn}
			disabled={disableAutomated}
			icon={<ExtensionMode />}
			description={i18nPackage.t("editQuizPage.methodStep.automatedProctoringDescription")}
			title={i18nPackage.t("editQuizPage.methodStep.automatedProctoringHeader")}
			sx={{
				height: isMobileLayout ? "fit-content" : "90px",
				width: "100%",
			}}
		/>
	);

	const disableLive =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLiveProctoring ||
		!enabledProctoringOptions.proctoringMethod.includes("live");
	const liveButton = (
		<EditQuizButton
			onClick={() => {
				if (!options.isLiveProctored) {
					setLiveMode("live");
				}
			}}
			checked={options.isLiveProctored ?? false}
			disabled={disableLive}
			sx={{ height: isMobileLayout ? "fit-content" : "90px", width: "100%" }}
			icon={<LiveProctorMode />}
			title={i18nPackage.t("editQuizPage.methodStep.liveProctoringHeader")}
			description={i18nPackage.t("editQuizPage.methodStep.liveProctoringDescription")}
		/>
	);

	const disablePopIn =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLivePopIn ||
		!enabledProctoringOptions.proctoringMethod.includes("pop-in");
	const popInButton = (
		<EditQuizButton
			onClick={() => {
				if (!options.isLivePopIn) {
					updateCompletedSteps((prev) => {
						const next = new Set(prev);
						// Remove live sessions step from completed steps if it is there
						// This happens if user created a live session, did all the other steps, but then decided to change to some other method
						next.delete("Live Sessions");
						return next;
					});
					setLiveMode("pop-in");
				}
			}}
			checked={(options.isLivePopIn && !options.enablePostReview) ?? false}
			disabled={disablePopIn}
			sx={{ height: isMobileLayout ? "fit-content" : "90px", width: "100%" }}
			icon={<LivePopInMode />}
			title={i18nPackage.t("editQuizPage.methodStep.liveCheckInHeader")}
			description={i18nPackage.t("editQuizPage.methodStep.liveCheckInDescription")}
		/>
	);

	const disablePopInPostReview =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLivePopIn ||
		!verityInstituteSettings.allowLivePopInPostReview ||
		!enabledProctoringOptions.proctoringMethod.includes("pop-in-post-review");
	const popInPostReviewButton = (
		<EditQuizButton
			onClick={() => {
				if (!options.isLivePopIn || !options.enablePostReview) {
					if (!options.isLivePopIn) {
						updateCompletedSteps((prev) => {
							const next = new Set(prev);
							// Remove live sessions step from completed steps if it is there
							// This happens if user created a live session, did all the other steps, but then decided to change to some other method
							next.delete("Live Sessions");
							return next;
						});
					}
					setLiveMode("pop-in-post-review");
				}
			}}
			checked={(options.isLivePopIn && options.enablePostReview) ?? false}
			disabled={disablePopInPostReview}
			sx={{ height: isMobileLayout ? "fit-content" : "90px", width: "100%" }}
			icon={<LivePopInPostReviewMode style={{ marginRight: "10px" }} />}
			title={i18nPackage.t("editQuizPage.methodStep.liveCheckInPostReviewHeader")}
			description={i18nPackage.t("editQuizPage.methodStep.liveCheckInPostReviewDescription")}
		/>
	);

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => <CorePaper className={styles.methodContainerMobile}>{children}</CorePaper>}
			whenFalse={(children) => <div className={styles.methodContainer}>{children}</div>}
		>
			<Typography className={styles.proctorSettingsGridPlatformsText}>
				{i18nPackage.t("editQuizPage.methodStep.header")}
			</Typography>

			<div className={styles.methodButtonTooltipWrapper}>
				{disableAutomated ? (
					<Tooltip title={i18nPackage.t("editQuizPage.methodStep.methodDisabled")}>
						<div style={{ width: "100%" }}>{automatedButton}</div>
					</Tooltip>
				) : (
					automatedButton
				)}
			</div>
			<div className={styles.methodButtonTooltipWrapper}>
				{disableLive ? (
					<Tooltip title={i18nPackage.t("editQuizPage.methodStep.methodDisabled")}>
						<div style={{ width: "100%" }}>{liveButton}</div>
					</Tooltip>
				) : (
					liveButton
				)}
			</div>
			<div className={styles.methodButtonTooltipWrapper}>
				{disablePopIn ? (
					<Tooltip title={i18nPackage.t("editQuizPage.methodStep.methodDisabled")}>
						<div style={{ width: "100%" }}>{popInButton}</div>
					</Tooltip>
				) : (
					popInButton
				)}
			</div>
			<div className={styles.methodButtonTooltipWrapper}>
				{disablePopInPostReview ? (
					<Tooltip title={i18nPackage.t("editQuizPage.methodStep.methodDisabled")}>
						<div style={{ width: "100%" }}>{popInPostReviewButton}</div>
					</Tooltip>
				) : (
					popInPostReviewButton
				)}
			</div>
			{deleteSessionsModalShowing && <DeleteLiveSessionsModal onClose={() => setDeleteSessionsModalShowing(false)} />}
		</ConditionalWrapper>
	);
}
